import {
    CreateButton,
    DataTable,
    DateField,
    ExportButton,
    List,
    NumberField,
    ReferenceField,
    SearchInput,
    SelectInput,
    TextField
} from '@/components/admin';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Target, TrendingUp } from 'lucide-react';
import { useGetList, useRecordContext } from 'ra-core';
import { TopToolbar } from '../layout/TopToolbar';

const StageBadge = () => {
    const record = useRecordContext();
    if (!record?.stage) return null;
    const colors: Record<string, string> = {
        'inquiry': 'bg-muted text-foreground border-border',
        'requirement_discussion': 'bg-info/10 text-info border-info/30',
        'proposal': 'bg-purple-50 text-purple-700 border-purple-200',
        'won': 'bg-success/10 text-success border-success/30',
        'lost': 'bg-destructive/10 text-destructive border-destructive/30',
    };
    const color = colors[record.stage] || colors['inquiry'];
    const label = record.stage.replace('_', ' ').toUpperCase();
    return <Badge variant="outline" className={`text-[10px] font-semibold ${color}`}>{label}</Badge>;
};

const OpportunityListActions = () => (
    <TopToolbar>
        <ExportButton />
        <CreateButton label="New Opportunity" />
    </TopToolbar>
);

const filters = [
    <SearchInput key="q" source="q" alwaysOn />,
    <SelectInput key="stage" source="stage" choices={[
        { id: 'inquiry', name: 'Inquiry' },
        { id: 'requirement_discussion', name: 'Requirement Discussion' },
        { id: 'proposal', name: 'Proposal' },
        { id: 'won', name: 'Won' },
        { id: 'lost', name: 'Lost' },
    ]} />,
];

const PipelineSummary = () => {
    const { data: opportunities, isPending } = useGetList('opportunities', {
        pagination: { page: 1, perPage: 1000 },
        filter: {},
    });

    if (isPending) return null;

    const stages = [
        { id: 'inquiry', label: 'Inquiry', color: 'bg-muted text-foreground outline outline-1 outline-border' },
        { id: 'requirement_discussion', label: 'Req. Discussion', color: 'bg-info/10 text-info outline outline-1 outline-info/30' },
        { id: 'proposal', label: 'Proposal', color: 'bg-purple-50 text-purple-700 outline outline-1 outline-purple-200' },
        { id: 'won', label: 'Won', color: 'bg-success/10 text-success outline outline-1 outline-success/30' },
        { id: 'lost', label: 'Lost', color: 'bg-destructive/10 text-destructive outline outline-1 outline-destructive/30' },
    ];

    return (
        <div className="flex gap-4 mb-6 overflow-x-auto pb-2 px-1">
            {stages.map(stage => {
                const opps = (opportunities || []).filter(o => o.stage === stage.id);
                const value = opps.reduce((sum, o) => sum + (o.deal_value || 0), 0);
                
                return (
                    <div key={stage.id} className="min-w-[180px] flex-1 bg-card rounded-xl shadow-sm overflow-hidden flex flex-col">
                        <div className={`px-4 py-2 text-[11px] font-bold uppercase tracking-wider ${stage.color}`}>
                            {stage.label} ({opps.length})
                        </div>
                        <div className="p-4 border-x border-b border-border flex-1 rounded-b-xl">
                            <div className="text-xl font-black text-foreground">₹{(value / 100000).toFixed(1)}L</div>
                            <div className="text-[10px] font-bold text-muted-foreground uppercase mt-0.5 tracking-wider">Pipeline Value</div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export const OpportunityList = () => (
    <List
        title="Opportunities"
        actions={<OpportunityListActions />}
        filters={filters}
        sort={{ field: 'created_at', order: 'DESC' }}
        className="p-6"
    >
        <PipelineSummary />
        <Card className="border-none shadow-sm overflow-hidden">
            <DataTable rowClick="show">
                <DataTable.Col
                    source="reference"
                    label="Opportunity No."
                    className="font-mono font-bold text-foreground"
                />
                <DataTable.Col label="Company">
                    <ReferenceField source="company_id" reference="companies" link={false}>
                        <TextField source="name" className="text-info font-medium" />
                    </ReferenceField>
                </DataTable.Col>
                <DataTable.Col label="Contact">
                    <ReferenceField source="contact_id" reference="contacts" link={false}>
                        <TextField source="first_name" />
                    </ReferenceField>
                </DataTable.Col>
                <DataTable.Col label="Stage">
                    <StageBadge />
                </DataTable.Col>
                <DataTable.Col label="Deal Value">
                    <div className="flex items-center gap-1 font-bold text-success">
                        ₹<NumberField source="deal_value" options={{ maximumFractionDigits: 0 }} />
                    </div>
                </DataTable.Col>
                <DataTable.Col label="Win Probability">
                    <div className="flex items-center gap-2">
                        <TrendingUp className="w-3 h-3 text-muted-foreground" />
                        <span className="font-semibold"><NumberField source="win_probability" />%</span>
                    </div>
                </DataTable.Col>
                <DataTable.Col label="Expected Close">
                    <div className="flex items-center gap-2 text-muted-foreground text-xs">
                        <Target className="w-3 h-3" />
                        <DateField source="expected_close_date" />
                    </div>
                </DataTable.Col>
            </DataTable>
        </Card>
    </List>
);
